import React, { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Password as PasswordIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import { useAuth } from "../../auth/AuthContext";
import { useError } from "../../error/useError";
import { useEmailChangeStatus, useCompleteEmailChange } from "../../hooks/user";
import { useUserProfileContext } from "../../contexts/ProfileContext";
import SurfaceCard from "../common/ui/SurfaceCard";
import ErrorAlert from "../common/ErrorAlert";
import ChangePasswordDialog from "./ChangePasswordDialog";
import ChangeEmailDialog from "./ChangeEmailDialog";
import CancelEmailChangeDialog from "./CancelEmailChangeDialog";

const SecurityOverviewSection: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { error, helpText, clearError, handleError } = useError();

  const [openChangePassword, setOpenChangePassword] = useState(false);
  const [openChangeEmail, setOpenChangeEmail] = useState(false);
  const [openCancelEmailChange, setOpenCancelEmailChange] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [countdown, setCountdown] = useState(5);

  const { userProfile } = useUserProfileContext();
  const { data: status = null, refetch: refreshStatus } = useEmailChangeStatus();
  const { mutate: completeEmailChange, isPending: isCompletingEmailChange } = useCompleteEmailChange();

  const hasPendingEmailChange = Boolean(status?.hasPendingChange);

  const handleSignIn = useCallback(() => {
    setShowSuccessDialog(false);
    navigate(LOGIN_ROUTE_PATH, {
      state: {
        message: "Email changed successfully. Please sign in with your new email.",
      },
    });
  }, [navigate]);

  useEffect(() => {
    if (!showSuccessDialog) {
      setCountdown(5);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSignIn();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showSuccessDialog, handleSignIn]);

  return (
    <Box sx={{ p: { xs: 1.2, md: 1.8 } }}>
      <ErrorAlert
        error={error}
        helpText={helpText}
        onClear={clearError}
        onRetry={() => refreshStatus()}
        title="Security Action Error"
      />

      <SurfaceCard
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
        }}
      >
        <Box sx={{ p: { xs: 2, md: 2.6 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.2}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            sx={{ mb: 2.2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: alpha(theme.palette.warning.main, 0.2),
                  color: theme.palette.warning.light,
                }}
              >
                <SecurityIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Security
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Protect your account credentials and login email.
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
              label={hasPendingEmailChange ? "Attention needed" : "Protected"}
              color={hasPendingEmailChange ? "warning" : "success"}
              variant={hasPendingEmailChange ? "filled" : "outlined"}
            />
          </Stack>

          <Stack spacing={1.2}>
            <Box
              sx={{
                p: 1.4,
                borderRadius: "10px",
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                background: alpha(theme.palette.warning.main, 0.08),
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }}>
                <Box>
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.4 }}>
                    <PasswordIcon fontSize="small" sx={{ color: theme.palette.warning.light }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Password and sign-in
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Update your password regularly to keep your account secure.
                  </Typography>
                </Box>

                <Button variant="outlined" onClick={() => setOpenChangePassword(true)} sx={{ textTransform: "none" }}>
                  Change password
                </Button>
              </Stack>
            </Box>

            <Box
              sx={{
                p: 1.4,
                borderRadius: "10px",
                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                background: alpha(theme.palette.info.main, 0.08),
              }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} sx={{ mb: 1 }}>
                <Box>
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.4 }}>
                    <EmailIcon fontSize="small" sx={{ color: theme.palette.info.light }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Email address
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {hasPendingEmailChange
                      ? "A change is pending. Verify both inboxes to complete it."
                      : "Use your email for sign-in and account recovery."}
                  </Typography>
                </Box>

                {hasPendingEmailChange ? (
                  <Chip size="small" label="Pending change" color="warning" />
                ) : (
                  <Chip size="small" label="Verified" color="success" variant="outlined" />
                )}
              </Stack>

              {!hasPendingEmailChange && userProfile?.email && (
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                    background: alpha(theme.palette.background.paper, 0.45),
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ wordBreak: "break-all", fontWeight: 600 }}>
                    {userProfile.email}
                  </Typography>
                </Box>
              )}

              {hasPendingEmailChange && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} sx={{ mb: 1 }}>
                  <Chip
                    label={status?.oldEmailVerified ? "Current email verified" : "Current email pending"}
                    color={status?.oldEmailVerified ? "success" : "default"}
                    variant={status?.oldEmailVerified ? "filled" : "outlined"}
                    size="small"
                  />
                  <Chip
                    label={status?.newEmailVerified ? "New email verified" : "New email pending"}
                    color={status?.newEmailVerified ? "success" : "default"}
                    variant={status?.newEmailVerified ? "filled" : "outlined"}
                    size="small"
                  />
                </Stack>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => setOpenChangeEmail(true)}
                  disabled={hasPendingEmailChange}
                  sx={{ textTransform: "none" }}
                >
                  Change email
                </Button>

                {hasPendingEmailChange && (
                  <>
                    <Button
                      variant="text"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setOpenCancelEmailChange(true)}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel pending change
                    </Button>

                    <Tooltip title={status?.canComplete ? "Both emails verified" : "Verify both emails to enable"}>
                      <span>
                        <Button
                          variant="contained"
                          disabled={!status?.canComplete || isCompletingEmailChange}
                          onClick={() => {
                            clearError();
                            completeEmailChange(undefined, {
                              onSuccess: async () => {
                                await logout();
                                setShowSuccessDialog(true);
                              },
                              onError: (apiError) => {
                                handleError(apiError);
                              },
                            });
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          {isCompletingEmailChange ? "Completing..." : "Complete email change"}
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </SurfaceCard>

      <ChangePasswordDialog open={openChangePassword} onClose={() => setOpenChangePassword(false)} />
      <ChangeEmailDialog
        open={openChangeEmail}
        onClose={() => {
          setOpenChangeEmail(false);
          refreshStatus();
        }}
        onInitiated={refreshStatus}
      />
      <CancelEmailChangeDialog
        open={openCancelEmailChange}
        onClose={() => setOpenCancelEmailChange(false)}
        onCompleted={refreshStatus}
      />

      <Dialog open={showSuccessDialog} onClose={() => {}} disableEscapeKeyDown maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Email Changed Successfully</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            For security reasons, you need to sign in again with your new email address.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Redirecting in <strong>{countdown}</strong> second{countdown !== 1 ? "s" : ""}...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={((5 - countdown) / 5) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="contained" onClick={handleSignIn} fullWidth>
            Sign In Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SecurityOverviewSection;
